import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { eventService } from '../../../services/eventService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const EventAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await eventService.getEventAnalytics(id);
        setStats(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const chartOptions = useMemo(() => {
    if (!stats || !stats.trends || stats.trends.length === 0) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Opens', 'QR Scans', 'Connections'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: stats.trends.map(t => new Date(t.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))
      },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        { name: 'Opens', type: 'line', data: stats.trends.map(t => t.opens), smooth: true, itemStyle: { color: '#3b82f6' } },
        { name: 'QR Scans', type: 'line', data: stats.trends.map(t => t.qr_scans), smooth: true, itemStyle: { color: '#a855f7' } },
        { name: 'Connections', type: 'line', data: stats.trends.map(t => t.connections), smooth: true, itemStyle: { color: '#f59e0b' } },
      ]
    };
  }, [stats]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const summary = stats?.summary || {};
  const hasData = summary.card_opens > 0 || summary.qr_scans > 0;
  
  const connRate = summary.card_opens > 0 ? Math.round((summary.connections / summary.card_opens) * 100) : 0;
  const scanToOpenRate = summary.qr_scans > 0 ? Math.round((summary.card_opens / summary.qr_scans) * 100) : 0;

  return (
    <div className="max-w-6xl pb-12">
      <div className="mb-6 flex items-center text-sm font-medium text-slate-500">
        <button onClick={() => navigate('/app/events')} className="hover:text-slate-900 flex items-center">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Events
        </button>
        <span className="mx-2">/</span>
        <button onClick={() => navigate(`/app/events/${id}`)} className="hover:text-slate-900">Event Detail</button>
        <span className="mx-2">/</span>
        <span className="text-slate-900">Analytics</span>
      </div>

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Event Analytics</h1>
        <p className="mt-2 text-slate-600">Measure engagement and conversion for this event.</p>
      </header>

      {!hasData ? (
        <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiIcons.FiBarChart2} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">No data collected yet</h3>
          <p className="text-slate-500 max-w-sm mx-auto">Interactions with event-linked cards and QR codes will appear here.</p>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <MetricCard label="QR Scans" value={summary.qr_scans} icon={FiIcons.FiMaximize} color="purple" />
            <MetricCard label="Card Opens" value={summary.card_opens} icon={FiIcons.FiEye} color="blue" />
            <MetricCard label="Connections" value={summary.connections} icon={FiIcons.FiUsers} color="amber" />
            <MetricCard label="CTA Clicks" value={summary.booking_clicks} icon={FiIcons.FiMousePointer} color="emerald" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-base font-bold text-slate-900 mb-6">Activity Timeline</h3>
              <div className="h-80 w-full">
                {chartOptions ? <ReactECharts option={chartOptions} style={{height: '100%', width: '100%'}} /> : <p>Not enough data</p>}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-base font-bold text-slate-900 mb-4">Conversion Funnel</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">Scan to Open</span>
                      <span className="font-bold text-slate-900">{scanToOpenRate > 100 ? 100 : scanToOpenRate}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${Math.min(scanToOpenRate, 100)}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">Open to Connection</span>
                      <span className="font-bold text-slate-900">{connRate > 100 ? 100 : connRate}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${Math.min(connRate, 100)}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, icon, color }) => {
  const colorMap = {
    blue: 'text-blue-600 bg-blue-50',
    purple: 'text-purple-600 bg-purple-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center sm:items-start text-center sm:text-left">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${colorMap[color]}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value || 0}</p>
      <p className="text-sm font-medium text-slate-500">{label}</p>
    </div>
  );
};